import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import { router } from "./router";
import vuetify from "./plugins/vuetify";
import axios from "axios";
import { setupAuthWatcher } from "./stores/authStoreRefactor";
import cable from "./services/cable";
import { sanitizeHtml } from "./utils/sanitizeHtml";
import { useDeadlinesStoreRefactor } from "./stores/deadlinesStoreRefactor";
import { useAccountsStoreRefactor } from "./stores/accountsStoreRefactor";
import { useNotificationsStoreRefactor } from "./stores/notificationsStoreRefactor";
import { useAccountLeadsStoreRefactor } from "./stores/accountLeadsStoreRefactor";
import { useAccountCategoriesStoreRefactor } from "./stores/accountCategoriesStoreRefactor";
import { useDayReportsStoreRefactor } from "./stores/dayReportsStoreRefactor";
import { getApiBaseUrl } from "./config/apiConfig";

// Register service worker for production only
if (import.meta.env.PROD) {
  import("./registerServiceWorker");
}

// Configure axios base URL
// - Render.com: uses VITE_API_URL env var (full backend URL)
// - CloudFront/Vite: empty string (proxy/rewrite handles /api/*)
axios.defaults.baseURL = getApiBaseUrl();
axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.headers.common["Content-Type"] = "application/json";

// Import new Vue 3 compatible plugins
import VueCountryFlag from "vue-country-flag-next";
import V3InfiniteLoading from "v3-infinite-loading";
import Vue3MarkdownIt from "vue3-markdown-it";
// ActionCable is not being used at the moment

// Import moment directly
import moment from "moment";
import "moment/locale/nl";

// Create app instance
const app = createApp(App);

// Create Pinia instance
const pinia = createPinia();
app.use(pinia);

// Initialize tenant store for global access
import { useTenantStore } from "./stores/tenantStore";
const tenantStore = useTenantStore();

// Make tenant store available globally for debugging
window.tenantStore = tenantStore;

// Setup auth watcher immediately (now synchronous) before any API calls
try {
  const authStore = setupAuthWatcher(pinia);

  // Make authStore available globally for debugging
  window.authStore = authStore;

  // Initialize authentication state - this will fetch user data if token exists
  authStore.initializeAuth();
} catch (error) {
  console.error("Failed to setup auth watcher:", error);
}

// Use other plugins
app.use(router);
app.use(vuetify);
app.use(VueCountryFlag);
app.use(Vue3MarkdownIt);

// Register global components
app.component("V3InfiniteLoading", V3InfiniteLoading);

// Use moment globally if needed
moment.locale("nl");

// Make ActionCable available globally
app.config.globalProperties.$cable = cable;

// Make the HTML sanitizer available to every template. All `v-html` bindings
// of user-generated content must pass through `$sanitizeHtml(...)` to prevent
// stored XSS. See src/utils/sanitizeHtml.js.
app.config.globalProperties.$sanitizeHtml = sanitizeHtml;

// Make build info available globally
app.config.globalProperties.$buildInfo = {
  version: __APP_VERSION__, // eslint-disable-line no-undef
  timestamp: __BUILD_TIMESTAMP__, // eslint-disable-line no-undef
  hash: __BUILD_HASH__, // eslint-disable-line no-undef
};

// Ensure public tenant info is loaded before mounting for consistent branding
(async () => {
  try {
    await tenantStore.load();
    // Update document title with tenant name
    tenantStore.updateDocumentTitle();
  } catch (e) {
    // Proceeding without dynamic tenant data
  }

  // Mount the app
  app.mount("#app");
})();
